/**
 * FunctionCallCache
 * -----------------------------------------------------------------------------
 * Central cache + orchestration layer for custom-function calls in Excel.
 *
 * ## What it does
 * - Builds **stable function keys** with `KeyBuilder` and stores the call state
 *   + result for each key.
 * - Tracks whether a call is `stale`, `fetching`, `ready`, or `error`.
 * - Exposes **Excel-friendly values** (including emojis for refresh prompts and
 *   proper error propagation via `CustomFunctions.Error`).
 * - Generates **pending API work items** (`ApiQuery[]`) without parsing keys,
 *   using metadata captured at registration time.
 * - Supports **auto-refresh windows** scoped to Workbook / Worksheet / Range,
 *   using locations from `FunctionLocationTracker` to decide which keys qualify.
 * - (Optional) **Render plugin mode**: retrieves a per-user token from
 *   `customXmlParts`, and triggers a one-time workbook-wide refresh after the
 *   first full calculation finishes.
 *
 * ## State model
 * - `cache: Map<FunctionKey, FunctionCallEntry>`
 *   - `status: 'stale' | 'fetching' | 'ready' | 'error'`
 *   - `data: any | null`
 *   - `errorMessage?: string`
 *   - `autoRefresh: boolean` (only used when status is `stale`)
 * - `keyMeta: Map<key, { functionName: string; params: Record<string, any> }>`
 *   (keeps `_buildApiCallListFromKeys` O(n) without key parsing)
 *
 * ## Key building
 * - `buildKey(funcName, args)` delegates to `KeyBuilder.build(funcName, args)`.
 *   Use this consistently so keys match what `FunctionLocationTracker` stores.
 *
 * ## Auto-refresh
 * - Call `FunctionCallCache.setAutoRefreshArea(area)` to open a 10s window
 *   (configurable via `AUTO_REFRESH_INTERVAL`) during which newly-stale keys
 *   *within that area* are marked `autoRefresh: true` and enqueued.
 * - Areas:
 *   - `Workbook` (all keys qualify)
 *   - `Worksheet` (by `worksheetId` preferred; falls back to name)
 *   - `Range` (checks cell containment via `excel-helpers`)
 * - Batching: a `DataLoader` plus `pRetry` drives `refreshAllAutoRefreshAlphaLinks`
 *   with deduping, backoff and up to 5 retries.
 *
 * ## Excel return helpers
 * - `getValueForExcel(funcName, args)` (or `...ByKey`) returns:
 *   - `ready`  → `entry.data`
 *   - `error`  → throws `CustomFunctions.Error(notAvailable, message)`
 *   - `stale`  → `"🔄"` (or `"⏳"` if auto-refreshing)
 * - Date convenience: if a string result is ISO date-like, it is boxed as
 *   `{ type: 'Double', basicValue: excelSerial, numberFormat: 'm/d/yyyy' }`
 *   so Excel displays a proper date.
 *
 * ## Applying results
 * - `applyApiResults(results: ApiResult[])` consumes server responses keyed by
 *   `key` and flips entries to `ready` or `error` accordingly.
 *
 * ## Registration & lookups
 * - `registerCall(funcName, args)` returns `{ key, created }` and records meta.
 * - `registerByKey(key, meta?)` is available when the caller already has a key.
 * - `hasCall/getEntry/...` provide read access by `(funcName, args)`.
 *
 * ## Pending work (no key parsing)
 * - `getPendingApiCallsForWorkbook/Sheet/SheetId/Cell()` gather keys from
 *   `FunctionLocationTracker`, then `_buildApiCallListFromKeys()` emits
 *   `ApiQuery[]` for entries that are not `ready` or `fetching`.
 *
 * ## Render plugin mode (optional)
 * - `setRenderPluginMode(true)`:
 *   - Reads a user token from `customXmlParts` (`http://alphascript.io/userToken`)
 *   - Attaches `worksheets.onCalculated` to detect first completed calculation,
 *     then triggers a one-off `refreshWorkbookAlphaLinks()` (guarded by
 *     `alreadyRefreshed`).
 *
 * ## Maintenance
 * - `cleanupUnusedCalls()` prunes cache/meta keys that no longer appear in the
 *   workbook (per `FunctionLocationTracker.getAllMappings()`).
 * - `clear()` wipes in-memory state (useful for dev/teardown).
 *
 * ## Glyphs
 * - `🔄` — stale; waiting for user to refresh (or auto-refresh window)
 * - `⏳` — stale but auto-refresh in progress
 *
 * ## Complexity (typical)
 * - Registration / state updates: O(1) amortized
 * - Excel return / lookups by key: O(1)
 * - Building API lists: O(N) over provided keys (no parsing)
 *
 * ## Example
 * ```ts
 * const calls = FunctionCallCache.getInstance();
 *
 * // When a formula is written:
 * const { key } = calls.registerCall("share_price_data", { ticker: "AAPL", ccy: "USD" });
 *
 * // On recalc you might fetch if needed, then:
 * calls.setSuccessByKey(key, { price: 192.34 });
 * // or
 * calls.setErrorByKey(key, "Upstream API error");
 *
 * // Excel runtime reads the cell value:
 * const value = calls.getValueForExcel("share_price_data", { ticker: "AAPL", ccy: "USD" });
 *
 * // During a user-initiated refresh on the active sheet:
 * FunctionCallCache.setAutoRefreshArea({ type: "Worksheet", worksheetId: activeId, worksheetName: activeName });
 * calls.setAutoRefresh(key); // marks ⏳ and enqueues a refresh batch
 * ```
 *
 * ## Notes
 * - Relies on Office.js Excel APIs (`Excel.run`, events, `CustomFunctions.Error`).
 * - Keep `FunctionLocationTracker` up to date (e.g., on formula writes/cleans),
 *   otherwise pending work builders may skip keys or fail to scope by area.
 * - Errors are surfaced to Excel as `#N/A` with a friendly message.
 */
import { decrementPendingBatch, incrementPendingBatch, refreshAllAutoRefreshAlphaLinks, refreshWorkbookAlphaLinks } from '../utils/alphaFunctions';
import { isIsoDate, jsDateToExcelSerial } from './date-helpers';
import { cellInRect, rangeToRect, splitSheetAndBody } from './excel-helpers';
import { FunctionLocationTracker } from './functionLocationTracker';
import DataLoader from 'dataloader';
import pRetry from 'p-retry';
import { KeyBuilder } from './keyBuilder';
export class FunctionCallCache {
    // ── Singleton (sync)
    static instance = null;
    static getInstance() {
        return this.instance ?? (this.instance = new FunctionCallCache());
    }
    constructor() { }
    // ── State
    static alreadyRefreshed = false;
    static autoRefreshArea = null;
    static autoRefreshUntil = null;
    static AUTO_REFRESH_INTERVAL = 30_000;
    static USER_TOKEN = null;
    static RENDER_PLUGIN_MODE = false;
    cache = new Map();
    // New: metadata map to avoid key parsing anywhere
    keyMeta = new Map();
    static STATUS_GLYPH = {
        refresh: '🔄',
        autorefreshing: '⏳',
    };
    static autoRefreshBatcher = new DataLoader(async (keys) => {
        const unique = [...new Set(keys)];
        incrementPendingBatch();
        try {
            await pRetry(() => refreshAllAutoRefreshAlphaLinks(unique), {
                retries: 5,
                minTimeout: 100,
                maxTimeout: 2000,
            });
        }
        finally {
            decrementPendingBatch();
        }
        return new Array(keys.length).fill(undefined);
    }, { maxBatchSize: 500, batchScheduleFn: (dispatch) => setTimeout(dispatch, 2000) });
    // ── Optional workbook hooks
    static setAutoRefreshUntil() {
        this.autoRefreshUntil = new Date(Date.now() + this.AUTO_REFRESH_INTERVAL);
    }
    static isAutoRefreshWindowOpen() {
        return !!this.autoRefreshUntil && Date.now() < this.autoRefreshUntil.getTime();
    }
    static setAutoRefreshArea(area) {
        this.setAutoRefreshUntil();
        this.autoRefreshArea = area;
    }
    static async setRenderPluginMode(enabled = false) {
        this.RENDER_PLUGIN_MODE = enabled;
        if (!enabled)
            return;
        this.USER_TOKEN = await this.readUserToken();
        await this.addOnAllFunctionRegistered();
    }
    static async readUserToken() {
        return Excel.run(async (ctx) => {
            const parts = ctx.workbook.customXmlParts.getByNamespace('http://alphascript.io/userToken');
            parts.load('items/id');
            await ctx.sync();
            if (parts.items.length === 0)
                return null;
            const xml = parts.items[0].getXml();
            await ctx.sync();
            const match = xml.value.match(/<userToken[^>]*>(.*?)<\/userToken>/);
            return match ? match[1] : null;
        });
    }
    static async addOnAllFunctionRegistered() {
        await Excel.run(async (ctx) => {
            const handler = async () => {
                await Excel.run(async (inner) => {
                    const app = inner.application;
                    app.load('calculationState');
                    await inner.sync();
                    if (app.calculationState === Excel.CalculationState.done) {
                        inner.workbook.worksheets.onCalculated.remove(handler);
                        await inner.sync();
                        if (!FunctionCallCache.alreadyRefreshed) {
                            FunctionCallCache.alreadyRefreshed = true;
                            await refreshWorkbookAlphaLinks();
                        }
                    }
                });
            };
            ctx.workbook.worksheets.onCalculated.add(handler);
            await ctx.sync();
        });
    }
    // ── Keys
    buildKey(funcName, args) {
        return KeyBuilder.build(funcName, args);
    }
    // ── Auto-refresh (decision sync)
    shouldAutoRefresh(key) {
        if (!FunctionCallCache.isAutoRefreshWindowOpen())
            return false;
        const area = FunctionCallCache.autoRefreshArea;
        if (!area)
            return false;
        if (area.type === 'Workbook')
            return true;
        const tracker = FunctionLocationTracker.getInstance();
        if (area.type === 'Worksheet') {
            // Prefer the id bucket; if empty, try the name bucket; else fallback to all
            const locsById = area.worksheetId ? tracker.getLocationsForKeyOnSheet(key, area.worksheetId) : [];
            const locsByName = locsById.length === 0 && area.worksheetName ? tracker.getLocationsForKeyOnSheet(key, area.worksheetName) : [];
            const candidates = locsById.length > 0 ? locsById : locsByName.length > 0 ? locsByName : tracker.getLocationsForKey(key);
            if (candidates.length === 0)
                return false;
            return candidates.some((l) => (area.worksheetId && l.sheetId === area.worksheetId) || (!area.worksheetId && l.sheet === area.worksheetName));
        }
        if (area.type === 'Range') {
            const locsById = area.worksheetId ? tracker.getLocationsForKeyOnSheet(key, area.worksheetId) : [];
            const locsByName = locsById.length === 0 && area.worksheetName ? tracker.getLocationsForKeyOnSheet(key, area.worksheetName) : [];
            const candidates = locsById.length > 0 ? locsById : locsByName.length > 0 ? locsByName : tracker.getLocationsForKey(key);
            if (candidates.length === 0)
                return false;
            // Build rect only if we found candidates to test
            const { body } = splitSheetAndBody(area.rangeAddress);
            const rect = rangeToRect(body);
            return candidates.some((l) => {
                const sameSheet = (area.worksheetId && l.sheetId === area.worksheetId) || (!area.worksheetId && l.sheet === area.worksheetName);
                return sameSheet && cellInRect(l.address, rect);
            });
        }
        return false;
    }
    setAutoRefresh(key) {
        const ok = this.shouldAutoRefresh(key);
        if (!ok)
            return;
        const entry = this.cache.get(key);
        if (entry && entry.status === 'stale') {
            entry.autoRefresh = true;
            void this.scheduleAutoRefresh(key);
        }
    }
    async scheduleAutoRefresh(key) {
        FunctionCallCache.setAutoRefreshUntil();
        await FunctionCallCache.autoRefreshBatcher.load(key);
    }
    // ── Registration / lookups (no parsing)
    registerByKey(key, meta) {
        let created = false;
        if (!this.cache.has(key)) {
            this.cache.set(key, { status: 'stale', data: null, autoRefresh: false });
            created = true;
        }
        if (meta)
            this.keyMeta.set(key, meta);
        return { key, created };
    }
    registerCall(funcName, args) {
        const key = this.buildKey(funcName, args);
        const res = this.registerByKey(key);
        // Always store meta (keeps _buildApiCallListFromKeys O(n) with no parsing)
        this.keyMeta.set(key, { functionName: funcName, params: args });
        return res;
    }
    hasCall(funcName, args) {
        return this.cache.has(this.buildKey(funcName, args));
    }
    getEntry(funcName, args) {
        return this.cache.get(this.buildKey(funcName, args)) ?? null;
    }
    // ── Result updates (by key OR by (fn,args))
    setSuccess(funcName, args, result) {
        this.setSuccessByKey(this.buildKey(funcName, args), result);
        // meta already set at registerCall
    }
    setSuccessByKey(key, result) {
        let boxed = result;
        if (typeof result === 'string' && isIsoDate(result)) {
            boxed = { type: 'Double', basicValue: jsDateToExcelSerial(new Date(result)), numberFormat: 'm/d/yyyy' };
        }
        this.cache.set(key, { status: 'ready', data: boxed, autoRefresh: false });
    }
    setError(funcName, args, message) {
        this.setErrorByKey(this.buildKey(funcName, args), message);
    }
    setErrorByKey(key, message) {
        this.cache.set(key, { status: 'error', data: null, errorMessage: message, autoRefresh: false });
    }
    markStale(funcName, args) {
        const key = this.buildKey(funcName, args);
        const entry = this.cache.get(key);
        if (entry)
            entry.status = 'stale';
    }
    // ── Excel return helpers
    getValueForExcel(funcName, args) {
        return this.getValueForExcelByKey(this.buildKey(funcName, args));
    }
    getValueForExcelByKey(key) {
        const entry = this.cache.get(key);
        if (!entry)
            return FunctionCallCache.STATUS_GLYPH.refresh;
        switch (entry.status) {
            case 'ready':
                return entry.data;
            case 'error':
                throw new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable, entry.errorMessage || 'Error occurred, please refresh');
            case 'stale':
            default:
                return entry.autoRefresh ? FunctionCallCache.STATUS_GLYPH.autorefreshing : FunctionCallCache.STATUS_GLYPH.refresh;
        }
    }
    // ── Apply API results (no parsing)
    async applyApiResults(results) {
        for (const { key, success, error, data } of results) {
            if (success)
                this.setSuccessByKey(key, data);
            else
                this.setErrorByKey(key, error);
        }
    }
    // ── Introspection / stats
    getAllCalls() {
        const out = [];
        for (const [key, entry] of this.cache)
            out.push({ key, entry });
        return out;
    }
    getStats() {
        const stats = { ready: 0, fetching: 0, error: 0, stale: 0 };
        for (const entry of this.cache.values())
            stats[entry.status] = (stats[entry.status] || 0) + 1;
        return stats;
    }
    // ── Pending API builders (use metadata only; zero parsing)
    async getPendingApiCallsForWorkbook() {
        const tracker = FunctionLocationTracker.getInstance();
        const keys = new Set(tracker.getAllMappings().map((m) => m.key));
        return this._buildApiCallListFromKeys(keys);
    }
    async getPendingApiCallsForSheet(sheetName) {
        const tracker = FunctionLocationTracker.getInstance();
        const keys = new Set(tracker
            .getAllMappings()
            .filter((m) => m.sheet === sheetName)
            .map((m) => m.key));
        return this._buildApiCallListFromKeys(keys);
    }
    async getPendingApiCallsForSheetId(sheetId) {
        const tracker = FunctionLocationTracker.getInstance();
        const keys = new Set(tracker
            .getAllMappings()
            .filter((m) => m.sheetId === sheetId)
            .map((m) => m.key));
        return this._buildApiCallListFromKeys(keys);
    }
    async getPendingApiCallsForCell(address) {
        const tracker = FunctionLocationTracker.getInstance();
        const parsed = FunctionLocationTracker.parseAddress(address);
        if (!parsed)
            throw new Error(`Invalid address: ${address}`);
        const { sheetName, cellAddress } = parsed;
        const sheetId = await tracker.getSheetId(sheetName);
        if (!sheetId)
            throw new Error(`Could not find sheet ID for sheet: ${sheetName}`);
        const keys = new Set(tracker
            .getAllMappings()
            .filter((m) => m.sheetId === sheetId && m.address === cellAddress)
            .map((m) => m.key));
        return this._buildApiCallListFromKeys(keys);
    }
    _buildApiCallListFromKeys(keys) {
        const calls = [];
        for (const key of keys) {
            const meta = this.keyMeta.get(key);
            const entry = this.cache.get(key);
            if (!meta)
                continue; // key wasn’t registered (or was purged); skip
            if (entry?.status === 'ready' || entry?.status === 'fetching')
                continue;
            calls.push({ key, function: meta.functionName, params: meta.params });
        }
        return calls;
    }
    // ── Maintenance
    async cleanupUnusedCalls() {
        const tracker = FunctionLocationTracker.getInstance();
        const usedKeys = new Set(tracker.getAllMappings().map((m) => m.key));
        const removed = [];
        for (const key of Array.from(this.cache.keys())) {
            if (!usedKeys.has(key)) {
                this.cache.delete(key);
                this.keyMeta.delete(key);
                removed.push(key);
            }
        }
        return removed;
    }
    clear() {
        this.cache.clear();
        this.keyMeta.clear();
    }
    getMetaByKey(key) {
        return this.keyMeta.get(key) ?? null;
    }
    getReadyValueForUnlinkByKey(key) {
        const entry = this.cache.get(key);
        if (!entry) {
            return {
                ok: false,
                status: 'missing',
                reason: 'No cache entry exists for this Alpha function call.',
            };
        }
        if (entry.status === 'ready') {
            return {
                ok: true,
                data: entry.data,
            };
        }
        return {
            ok: false,
            status: entry.status,
            reason: entry.status === 'error'
                ? entry.errorMessage || 'The Alpha function returned an error.'
                : `The Alpha function value is ${entry.status}.`,
            errorMessage: entry.errorMessage,
        };
    }
}
//# sourceMappingURL=functionCallCache.js.map